import * as Sentry from '@sentry/react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { isAxiosError } from '@/api/axios/isAxiosErrorTypeGuard';
import { ApiError } from '@/api/types/ApiError';
import { createLogEvent } from '@/logging/createLogEvent';
import { addLogEvent } from '@/logging/logSlice';
import { tryGetPathnameFromUrl } from '@/logging/utils/tryGetPathnameFromUrl';
import { AppDispatch } from '@/store';

/**
 * Hook to handle async operations that may throw an error.
 *
 * @param initiate Callback to initiate the specified async operation.
 *
 * @returns A function that will initiate the async operation, a boolean that indicates loading state and
 * a boolean that indicates if there was an error.
 */
export const useHandleAsyncOperation = (
  initiate: () => Promise<void>,
): [() => Promise<void>, boolean, boolean] => {
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const initiateOperation = async (): Promise<void> => {
    setIsError(false);
    setIsLoading(true);

    try {
      await initiate();
      setIsError(false);
    } catch (error) {
      setIsError(true);
      console.error(error);

      if (isAxiosError(error)) {
        const apiError = new ApiError(
          {
            status: error.response?.status || 0,
            method: error.config?.method || 'N/A',
            url: error.config?.url || 'N/A',
            message: error.message,
            code: error.code,
          },
          error,
        );

        Sentry.captureException(apiError);

        const parsedUrl = tryGetPathnameFromUrl(apiError.url);

        dispatch(
          addLogEvent(
            createLogEvent({
              level: 'error',
              type: 'http',
              data: {
                message: apiError.originalMessage,
                method: apiError.method,
                status: apiError.status,
                code: apiError.code,
                url: parsedUrl,
              },
            }),
          ),
        );
      } else {
        Sentry.captureException(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return [initiateOperation, isLoading, isError];
};
